/**
 * ChangeOrderBuilderPage — Change Order builder with full QuoteBuilder parity.
 *
 * PT-45 (2026-07-29): Rewritten to reuse AreaCard, EstimatePanel, AddItemCascade,
 * and all quote-builder modals. Architecture: REUSE, not fork.
 *
 * Wire authority: CO-1-change-order.html State B.
 * Layout: 2-column grid (areas left, EstimatePanel right) matching QuoteBuilderPage.
 * Per-row affordances: "Change Item" / "Cancel Item" per CO-03.
 * Single red primary: "Review & Send" in EstimatePanel.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui/Button';
import PageHeader from '../../ui/PageHeader';
import Tabs from '../../ui/Tabs';
import DropdownMenu from '../../ui/DropdownMenu';
import MaterialListDialog from '../../quotes/builder/MaterialListDialog';
import StatusPill from '../../ui/StatusPill';
import { Alert } from '../../ui/Alert';
import { Card } from '../../ui/Card';
import AcknowledgmentsPanel from '../../acknowledgments/AcknowledgmentsPanel';
import { EmptyState } from '../../ui/EmptyState';
import { AttachModal } from '../../attachments/AttachModal';
import { entryDisplay, mergeDocumentEntries } from '../../../utils/attachmentEntry';
import { useToast } from '../../ui/Toast';
import { useOptionalAuth } from '../../../contexts/AuthContext';
import ReopenConfirmDialog, { canReopenCo, REOPEN_SUCCESS, REOPEN_FAILURE } from '../ReopenConfirmDialog';
import useBuilderCatalog from '../../../hooks/useBuilderCatalog';
import rescaleLineQuantity from '../../../quote-builder/rescaleLineQuantity';
import { changeOrderTaxCents, taxIsKnown, signedTaxRate, taxCaption } from '../../../change-order/signedTax';
import { resolveBuilderMarginContext } from '../../../lib/builderMarginContext';
import AreaCard from '../../quotes/builder/AreaCard';
import EstimatePanel from '../../quotes/builder/EstimatePanel';
import AddItemCascade from '../../quotes/builder/AddItemCascade';
import { CustomItemModal, NoteModal } from '../../quotes/builder/modals';
import api from '../../../services/api';
import { assembleChangeOrderPayload } from '../../../change-order/changeOrderSerializer';
import { useQuoteAutoSave } from '../../../quote-builder';
import { normalizeMarginValue } from '../../../quote-builder/quotePrefill';
import { groupLinesByArea } from '../../../quote-builder/groupLinesByArea';
import { applyLineEdit } from '../../../quote-builder/applyLineEdit';
import { rederiveCategoryMargins } from '../../../quote-builder/seedCatalog';
import { computeLineItem, costBreakdown } from '../../../quote-builder/marginEngine';
import { resolveAreaLabel } from './areaLabel';
import { coStatusLabel, coStatusTone, coTitle } from '../coDisplay';
import { getJobNumber } from '../../../utils/canonicalFields';
import {
  seedChangeOrderLine,
  computeChangeOrder,
  setLineStatus,
  undoLineChange,
  getLineCost,
  distributeChangeOrderToTarget,
  coTargetEditability as coTargetEditability_,
} from '../../../change-order/changeOrderEngine';

const CO_UNLOAD_PATH = (id) => `/v1/change-orders/${id}`;

// IDI-459 (parent IDI-455): the quote builder's tab layout (IDI-209), adopted
// per Adrian 08-28 — "we'll still follow that same tab".
//
// Deliberately NOT the quote's full set:
//   - no "Project Details": the project belongs to the JOB, not to a diff of
//     it, and customer editing is suppressed (E-32).
// IDI-542: the Acknowledgments tab landed with the change-order reader (same
// 'acknowledgements' value and label as the quote builder's).
const CO_TABS = [
  { value: 'builder', label: 'Builder' },
  { value: 'notes', label: 'Notes' },
  { value: 'files', label: 'Files' },
  { value: 'acknowledgements', label: 'Acknowledgments' },
];

// IDI-458: the caption under the rail's Tax row. States where the rate came
// from, so a reader can tell a real 0% from "we don't know".
function buildTaxNote(signedTax) {
  if (!taxIsKnown(signedTax)) {
    return signedTax?.source === 'unavailable'
      ? 'Tax unavailable: the signed quote could not be read.'
      : 'No tax rate: this job has no signed quote, no migrated rate and no default tax rate in Settings.';
  }
  // IDI-491: the shared caption ("Taxed at 6% (from the migrated quote)").
  if (signedTax?.source !== 'accepted_configuration') return taxCaption(signedTax);
  const rate = signedTaxRate(signedTax);
  if (rate <= 0) {
    // IDI-370/380: an EXEMPT quote and a 0%-jurisdiction quote both charge
    // nothing, but they are different facts. The backend distinguishes them;
    // say which one rather than implying an exemption that isn't recorded.
    return signedTax?.exempt
      ? 'Signed quote was tax exempt.'
      : 'Signed quote was rated at 0% tax.';
  }
  return `Taxed at ${(rate * 100).toFixed(3).replace(/\.?0+$/, '')}% from the signed quote.`;
}

// PR-13: consolidated — use hardened normalizer from quotePrefill.
const normalizeMarginPct = normalizeMarginValue;

// IDI-639 D6: a read-only CO's lines lock as a sent quote's do (IDI-398's
// lockedSectionProps): `inert` blocks every line menu, quantity button and
// Undo, and the section is dimmed. A local wrapper, so AreaCard and
// CommittedLineCard (shared with the quote builder) are untouched.
function lockedLinesProps(locked) {
  return locked
    ? { inert: '', 'aria-disabled': 'true', className: 'space-y-4 opacity-75', 'data-testid': 'co-lines-locked' }
    : { className: 'space-y-4' };
}

// IDI-556: CO status label/tone and title come from the one shared helper.

export default function ChangeOrderBuilderPage() {
  const { jobId, coId } = useParams();
  // IDI-459: the tab lives in the URL, as on the quote builder, so a reload
  // or a shared link lands on the same tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'builder';
  const handleTabChange = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useOptionalAuth();
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopening, setReopening] = useState(false);

  // ─── Core state ──────────────────────────────────────────────────────────────
  const [coLines, setCoLines] = useState([]);
  const [coMeta, setCoMeta] = useState(null); // { name, description, status, terms, attachments }
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // IDI-309: the estimate card re-prices added/changed lines
  // (distributeToTarget) instead of writing the lowest margin tier.
  // IDI-534: no hardcoded 35%. The overall (last-tier) margin is the Settings
  // dealer default, derived below from the shared catalog loader.

  // ─── Modal state ─────────────────────────────────────────────────────────────
  const [cascadeOpen, setCascadeOpen] = useState(false);
  // IDI-191: the line being replaced via "Change Item" (opens the Add-Item
  // wizard). null when the cascade is open for a plain "Add Item".
  // IDI-176 (2026-09-05): follow-through — the flat EditItemModal is now
  // retired here too; Edit Item opens a SECOND AddItemCascade instance in
  // mode='edit' (see below at line ~792). Same wizard shape as Change Item
  // and Add Item; three entry points, one editor.
  // IDI-459: `cascadeChangeTargetId` is gone with "Change Item" (E-27) — it
  // was that action's only entry point, so the replace branch it fed is
  // unreachable. `replaceChangeOrderLine` stays in the engine (still used by
  // the Edit path's tests) but this page no longer has a replace mode.
  const [editingItem, setEditingItem] = useState(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);

  const [noteModalItem, setNoteModalItem] = useState(null);

  // ─── Terms & Attachments state ───────────────────────────────────────────────
  // IDI-137 (D4): read-only here. Echoed on save so the serializer's
  // `terms ?? ''` can't wipe what Review & Send stored.
  const [terms, setTerms] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [materialListOpen, setMaterialListOpen] = useState(false);
  // IDI-460: real attachments, stored on the change order itself. The builder
  // previously held raw `File` objects in state and never uploaded them, so
  // they vanished on reload.
  const [attachOpen, setAttachOpen] = useState(false);
  const [coDocuments, setCoDocuments] = useState([]);
  // IDI-459: the Notes tab's content. It PERSISTS through the existing
  // autosave (see coPayload / CO_DIRTY_KEYS below) — shipping a textarea that
  // silently discarded what the user typed would be worse than no tab. The
  // customer-facing T&C half of IDI-455 AC-5 is still IDI-460's.
  const [internalNotes, setInternalNotes] = useState('');

  // ─── Data loading ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);

        // Load job
        const jobData = await api.getJob(jobId);
        if (cancelled) return;
        setJob(jobData);

        // Load customer
        if (jobData.customer_id || jobData.customerId) {
          try {
            const cust = await api.getCustomer(jobData.customer_id || jobData.customerId);
            if (!cancelled) setCustomer(cust);
          } catch { /* customer optional for pricing */ }
        }

        // Load CO if editing existing
        if (coId) {
          const coData = await api.getChangeOrder(coId);
          if (cancelled) return;
          setCoMeta({
            name: coData.name || coData.title || '',
            description: coData.description || '',
            status: coData.status || 'draft',
            // IDI-556: the stored number, for the "<name> · CO-000012" header.
            coNumber: coData.coNumber || coData.co_number || coData.change_order_number || '',
            terms: coData.terms || '',
            attachments: coData.attachments || [],
          });
          setTerms(coData.terms || '');
          setAttachments(coData.attachments || []);
          // IDI-459: read the notes back, so a reload shows what was saved.
          setInternalNotes(coData.internalNotes || coData.internal_notes || '');
          setCoDocuments(mergeDocumentEntries(coData));

          // If CO has saved lines, use them; otherwise seed from job
          if (coData.lines && coData.lines.length > 0) {
            // DEF-161 REPAIR-ON-OPEN: detect a corrupted draft CO (lines
            // seeded before c57186c, carrying zeros against real job money)
            // and re-seed from the job. Only DRAFT COs get re-seeded --
            // sent/accepted/approved/applied are historical records and
            // must NEVER be recomputed (they show the customer what was
            // quoted at that time).
            const jobLines = jobData.line_items || jobData.lineItems || [];
            const status = coData.status || 'draft';
            const isDraft = status === 'draft';

            // Corruption signature: every line's original.cost is 0 AND
            // every source job line has non-zero cost. This is the specific
            // shape produced by the pre-c57186c seed reading wrong keys.
            const looksCorrupted = isDraft && coData.lines.every(cl => {
              const oCost = cl.original?.cost;
              return oCost === 0 || oCost == null;
            }) && jobLines.some(jl => {
              // IDI-190: use the same 3-schema cost normalizer as
              // seedChangeOrderLine (getLineCost) so this detector also
              // recognizes the materialCostCents/totalLaborCostCents schema
              // (idi20_embed_from_line_item) — the old inline formula here
              // only checked materialCost/cost_basis/laborCost/otherCost and
              // would never fire for that schema, silently leaving a
              // corrupted draft un-repaired.
              const jc = getLineCost(jl).totalCost;
              return jc > 0;
            });

            if (looksCorrupted) {
              // Re-seed the draft from job data. Safe: a draft CO's lines
              // have no independent authority -- they mirror the job.
              // eslint-disable-next-line no-console
              console.warn('[DEF-161] draft CO carrying zeroed lines -- re-seeding from job', { coId: coData.id });
              setCoLines(jobLines.map((jl) => seedChangeOrderLine(jl, { tenantCategoryMargins: categoryMarginsRef.current })));
            } else {
              // Enrich saved lines with display fields from job (in case they
              // were saved before seedChangeOrderLine included productLabel etc.)
              const jobLineMap = {};
              jobLines.forEach(jl => { if (jl.id) jobLineMap[jl.id] = jl; });
              const enriched = coData.lines.map(cl => {
                const src = jobLineMap[cl.quoteLineId || cl.id];
                if (!src) return cl;
                return {
                  ...cl,
                  productLabel: cl.productLabel || src.productLabel || src.product_label || src.description || src.item_name,
                  areaLabel: cl.areaLabel || src.areaLabel || src.area_label,
                  areaId: cl.areaId || src.areaId || src.area_id,
                  categoryLabel: cl.categoryLabel || src.categoryLabel || src.category_label,
                  unit: cl.unit || src.unit,
                };
              });
              // IDI-534: a saved CO line may carry the old seed categoryMargin
              // (copied from a pre-#596 job line). Re-derive from Settings.
              setCoLines(rederiveCategoryMargins(enriched, categoryMarginsRef.current));
            }
          } else {
            const jobLines = jobData.line_items || jobData.lineItems || [];
            setCoLines(jobLines.map((jl) => seedChangeOrderLine(jl, { tenantCategoryMargins: categoryMarginsRef.current })));
          }
        } else {
          // New CO — seed from job lines
          const jobLines = jobData.line_items || jobData.lineItems || [];
          setCoLines(jobLines.map((jl) => seedChangeOrderLine(jl, { tenantCategoryMargins: categoryMarginsRef.current })));
        }

      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load change order');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [jobId, coId]);

  // ─── Catalog + pricing inputs (IDI-457, parent IDI-455) ─────────────────────
  // The SAME shared loader the quote builder uses, so Add Item here offers the
  // tenant's real pricebook (gated by IDI-373's offerability filters) instead
  // of the ~25 static sample products, and a line prices with the same inputs.
  const {
    accessoryCatalog,
    insulationCatalog,
    categoryMargins,
    laborDefaults: tenantLaborDefaults,
    customerTypes,
    catalogLoaded,
    settings: catalogSettings,
  } = useBuilderCatalog();
  // IDI-534 (G1): the dealer default margin — the last tier of the 10-02
  // order (category → customer → customer type → dealer default). Read from
  // the same Settings source and key as the quote builder's new-quote seed.
  // `useSettingsContext().settings` was always undefined (the context has no
  // `settings` field), so a line with no other tier priced at cost. Derived,
  // not set from an effect: it is ready in the render catalogLoaded flips,
  // and the CO dirty keys carry no margin, so opening still writes nothing.
  const overallMargin = useMemo(() => normalizeMarginPct(
    catalogSettings?.marginDefaults?.master_default_margin
      ?? catalogSettings?.master_default_margin,
  ), [catalogSettings]);
  // IDI-534 (REQ-002): the loader seeds with the latest Settings category
  // margins via this ref; the effect below re-derives once they arrive
  // (the catalog loads asynchronously and can land after the CO).
  const categoryMarginsRef = React.useRef(categoryMargins);
  categoryMarginsRef.current = categoryMargins;
  useEffect(() => {
    if (!catalogLoaded) return;
    setCoLines((prev) => rederiveCategoryMargins(prev, categoryMargins));
  }, [catalogLoaded, categoryMargins, loading]);
  // Only stop re-seeding the autosave baseline once the lines ALREADY carry
  // the re-derived margins (same render would read it as a user edit).
  const [marginsSettled, setMarginsSettled] = useState(false);
  useEffect(() => {
    if (marginsSettled || !catalogLoaded || loading) return;
    if (rederiveCategoryMargins(coLines, categoryMargins) === coLines) setMarginsSettled(true);
  }, [marginsSettled, catalogLoaded, loading, coLines, categoryMargins]);

  // ─── Margin context (PT-30 parity) ──────────────────────────────────────────
  // IDI-457 / IDI-99: this used to read `getSettingsOptions('customer_types')`
  // — the snake_case key. IDI-379 established that Settings stores the list
  // under `customerTypes` (camelCase; snake_case is ABSENT on tenant 1), so
  // the lookup matched nothing and the customer-type margin NEVER reached a
  // change order. Both builders now go through one resolver.
  const marginContext = useMemo(() => resolveBuilderMarginContext({
    customerMargin: customer?.default_margin,
    customerType: customer?.customer_type || customer?.type,
    customerTypes,
    overallMargin,
  }), [customer, customerTypes, overallMargin]);

  // ─── Compute CO totals ───────────────────────────────────────────────────────
  const co = useMemo(() => computeChangeOrder(coLines, marginContext), [coLines, marginContext]);

  // ─── DEF-160: Autosave (inherits useQuoteAutoSave per SD-1) ──────────────────
  // Dirty-check keys: only USER-EDITABLE fields. Computed totals (netTotal,
  // originalTotal, revisedTotal) are deliberately EXCLUDED — they participate
  // in the WRITE (via transformPayload) but NOT the dirty-check. Including
  // computed values here is the DEF-155 loop shape. `lines` already triggers
  // on any line edit; the totals add nothing to detection.
  const CO_DIRTY_KEYS = useMemo(() => [
    'lines', 'terms', 'attachments', 'status', 'internalNotes',
  ], []);

  // The payload memo that the autosave hook watches for changes.
  const coPayload = useMemo(() => ({
    lines: coLines,
    terms,
    attachments,
    status: coMeta?.status || 'draft',
    internalNotes,
    netTotal: co?.netTotal ?? 0,
    originalTotal: co?.originalTotal ?? 0,
    revisedTotal: co?.revisedTotal ?? 0,
    // IDI-454: the engine-computed lines must travel with the payload, or
    // the autosave path (the common one) serializes an added line with
    // `lineTotal: null` and the change order bills $0. handleSave passed the
    // whole `co` and was therefore correct; transformPayload rebuilt `co`
    // from the three totals and dropped the per-line prices. Excluded from
    // CO_DIRTY_KEYS for the same reason the totals are: computed values
    // don't belong in dirty detection (`lines` already covers every edit).
    computedLines: co?.lines ?? [],
  }), [coLines, terms, attachments, internalNotes, coMeta?.status, co?.netTotal, co?.originalTotal, co?.revisedTotal, co?.lines]);

  // Read-only guard: don't autosave sent/accepted/approved/applied COs.
  // IDI-595: a declined (Rejected) CO is read-only exactly like a Sent one.
  const isReadOnly = ['sent', 'accepted', 'approved', 'applied', 'declined'].includes(coMeta?.status);

  // IDI-309 (REQ-006): surface the autosave error; a silent failure looked
  // exactly like a save that changed nothing.
  const { isDirty, onLeave, error: autoSaveError } = useQuoteAutoSave(coId, coPayload, {
    toast,
    enabled: !loading && !isReadOnly && !!coId,
    // IDI-534: the Settings category-margin re-derive lands with the catalog;
    // pin the dirty baseline until then so opening a CO writes nothing.
    hydrated: marginsSettled,
    apiUpdate: (id, data) => api.updateChangeOrder(id, data),
    apiCreate: null, // COs are always pre-created via COCreateSheet
    // IDI-336: tab-close / Back keepalive save must hit the CO route.
    unloadPath: CO_UNLOAD_PATH,
    dirtyCheckKeys: CO_DIRTY_KEYS,
    useAliases: false, // CO fields are canonical; no alias reconciliation needed
    transformPayload: (data, id) => assembleChangeOrderPayload({
      coId: id || coId,
      jobId,
      coLines: data.lines,
      co: {
        netTotal: data.netTotal,
        originalTotal: data.originalTotal,
        revisedTotal: data.revisedTotal,
        lines: data.computedLines, // IDI-454: per-line engine prices
      },
      terms: data.terms,
      attachments: data.attachments,
      status: data.status,
      internalNotes: data.internalNotes,
    }),
  });

  // ─── Area grouping (identical pattern to QuoteBuilderPage) ───────────────────
  const areaGroups = useMemo(
    // §358 (2026-08-19): grouping goes through the shared `groupLinesByArea`
    // helper. See QuoteBuilderPage sibling comment for the full rationale.
    () => groupLinesByArea(
      coLines,
      // IDI-459: never render a raw id as a group header. `areaLabel` is
      // absent on plenty of CO lines (and on migrated job lines), and the old
      // fallback put the bare areaId — or 'uncategorized' — in front of the
      // user. Fall back to the area's name from the merged area list, then to
      // a plain word.
      (item, areaId) => ({
        areaId,
        label: resolveAreaLabel(item, areaId),
        items: [],
        computedItems: [],
      }),
      (g, item, idx) => { g.items.push(item); g.computedItems.push(co.lines[idx] || null); },
    ),
    [coLines, co.lines],
  );

  // ─── EstimatePanel summary (maps CO shape → EstimatePanel shape) ─────────────
  const estimateSummary = useMemo(() => {
    // EstimatePanel expects: costBreakdown, blendedMargin, bidSubtotal, taxAmount, bidTotal, marginDollars, isMarginEditable
    const bidSubtotal = co.revisedTotal || 0;
    const originalTotal = co.originalTotal || 0;
    const netTotal = co.netTotal || 0;
    // IDI-190 (stakeholder ruling): the Cost Breakdown reflects the CHANGE
    // being made, not the whole job's cost. On a freshly-opened CO nothing
    // has changed yet, so this must read $0.00 -- summing raw per-line cost
    // (the old formula) showed the full original job's cost on day one,
    // which is wrong for a document whose entire purpose is showing what's
    // DIFFERENT. `co.lines` (from computeChangeOrder) already carries a
    // per-line `costDelta` (revisedCost - originalCost, zero for untouched
    // lines) computed the same qty-ratio/cancelled-aware way as the revenue
    // delta -- sum THAT instead.
    const totalCost = co.lines.reduce((sum, l) => sum + (l.costDelta || 0), 0);

    // IDI-457: the cost and the revenue must describe the SAME thing — the
    // change. `totalCost` is the cost DELTA (IDI-190), so the revenue it is
    // measured against has to be the revenue delta (`netTotal`), not the
    // whole revised total. Comparing a delta cost against a full total made
    // the margin read ~100% on every change order (a $250 added line with
    // $185 of cost showed ~96% instead of 26%).
    //
    // A fresh CO has netTotal 0 and totalCost 0, so it shows 0% — the
    // IDI-190 ruling that a CO reflects the change, not the whole job.
    const blendedMargin = netTotal !== 0 ? (netTotal - totalCost) / netTotal : 0;
    const marginDollars = netTotal - totalCost;
    const taxAmount = changeOrderTaxCents(netTotal, job?.signedTax);

    // IDI-814 (D3, D4): Total Cost is the change's cost (above). Lines this CO
    // adds (no job original, not cancelled) split as Add Item priced them, by
    // the quote's own rule (computeLineItem, then costBreakdown, no burden), so
    // the parts equal the cost the delta used. A carried line's change stays
    // under Material (the job stores one cost), so the rows sum to Total Cost.
    const added = costBreakdown(
      coLines
        .filter((l) => !l.original && l.status !== 'cancelled')
        .map((l) => computeLineItem(l, marginContext)),
      0,
    );
    // Shares only above $0; EstimatePanel hides them when totalCost <= 0.
    const share = (v) => (totalCost > 0 ? v / totalCost : 0);
    const breakdown = {
      // Intentional (D4): every non-added change (carried, cancelled) is
      // Material, because a job line stores a single cost_basis.
      material: totalCost - added.labor - added.other,
      labor: added.labor,
      other: added.other,
      totalCost,
    };
    breakdown.materialPct = share(breakdown.material);
    breakdown.laborPct = share(breakdown.labor);
    breakdown.otherPct = share(breakdown.other);

    return {
      costBreakdown: breakdown,
      blendedMargin,
      // The rail's headline figure is the CHANGE, matching the net-change
      // totals strip below it (E-22) and what the customer is being asked to
      // approve.
      bidSubtotal: netTotal,
      // IDI-458: the net change is taxed at the rate locked on the job's
      // SIGNED quote (ruled 2026-10-06), which the job read now exposes as
      // `signedTax`. Applied by change-order/signedTax.js, whose Go
      // counterpart (internal/signedtax) is what cmd/invoices bills with —
      // so what this rail shows is what the invoice charges.
      taxAmount,
      bidTotal: netTotal + taxAmount,
      marginDollars,
      isMarginEditable: true,
      // CO-specific extras for display
      originalTotal,
      revisedTotal: bidSubtotal,
      netTotal,
      // So the rail states the BASIS rather than presenting a bare number,
      // and says plainly when the signed position isn't known — a confident
      // $0.00 is how a taxable change order gets under-billed.
      taxNote: buildTaxNote(job?.signedTax),
    };
  }, [co, coLines, job, marginContext]);

  // ─── IDI-309 / F-50: estimate-card target edit ───────────────────────────────
  // The card's headline is the NET CHANGE (revised - original), so that is the
  // target. Only lines this change order adds or changes are re-priced;
  // unchanged, locked and cancelled lines (and category-margin / Option
  // lines, as on a quote) count toward the fixed portion. The new price is
  // written onto the working line's lineTotal and persisted through `lines`.
  const coTargetEditability = useMemo(() => coTargetEditability_(coLines), [coLines]);
  const handleApplyCoTarget = useCallback(({ subtotalCents }) => {
    const result = distributeChangeOrderToTarget(coLines, subtotalCents, marginContext);
    if (result.ok && !result.unchanged) setCoLines(result.lines);
    return result;
  }, [coLines, marginContext]);

  // ─── Line handlers ───────────────────────────────────────────────────────────
  const updateLine = useCallback((id, patch) => {
    setCoLines((lines) => lines.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }, []);

  // eslint-disable-next-line no-unused-vars -- reserved for row-menu wiring (see DEF-nnn candidate: ChangeOrder audit / PT-53A handshake)
  const handleCancelItem = useCallback((id) => {
    setCoLines((lines) => lines.map((l) => (l.id === id ? setLineStatus(l, 'cancelled') : l)));
  }, []);

  const handleUndoChange = useCallback((id) => {
    setCoLines((lines) => lines.map((l) => (l.id === id ? undoLineChange(l) : l)));
  }, []);

  // ─── Edit item (via AddItemCascade mode='edit', post-IDI-176) ─
  const handleEditItemSave = useCallback((itemId, updates) => {
    setCoLines((lines) => lines.map((l) => {
      if (l.id !== itemId) return l;
      // IDI-534: route through applyLineEdit as quotes do (IDI-365 / 09-11):
      // an edit drops a one-time target re-price, so the line snaps back to
      // its own margin tier instead of keeping a stale price_override.
      const updated = applyLineEdit(l, updates);
      // If this was an original line and values changed, mark as 'changed'
      if (l.original && l.status !== 'added') {
        return setLineStatus(updated, 'changed');
      }
      return updated;
    }));
    setEditingItem(null);
  }, []);

  // ─── Remove item (only for added lines) ─────────────────────────────────────
  const handleRemoveItem = useCallback((itemId) => {
    setCoLines((lines) => {
      const line = lines.find((l) => l.id === itemId);
      if (!line) return lines;
      // Added lines can be fully removed; original lines get cancelled
      if (line.status === 'added' || !line.original) {
        return lines.filter((l) => l.id !== itemId);
      }
      return lines.map((l) => (l.id === itemId ? setLineStatus(l, 'cancelled') : l));
    });
  }, []);

  // ─── Add item (via AddItemCascade) ──────────────────────────────────────────
  const handleAddItem = useCallback((item) => {
    setCoLines((lines) => {
      // IDI-459: always an append. The in-place replace branch belonged to
      // "Change Item" (E-27, removed by the 2026-09-10 ruling).
      return [
        ...lines,
        {
          id: item.id || `new-${Date.now()}`,
          ...item,
          status: 'added',
          locked: false,
          original: null,
        },
      ];
    });
    setCascadeOpen(false);
  }, []);

  // ─── Note editing ───────────────────────────────────────────────────────────
  const handleNoteSave = useCallback((note) => {
    if (noteModalItem) {
      updateLine(noteModalItem.id, { notes: note });
    }
    setNoteModalItem(null);
  }, [noteModalItem, updateLine]);

  // ─── Margin update (per-line) ───────────────────────────────────────────────
  const handleUpdateMargin = useCallback((itemId, margin) => {
    setCoLines((lines) => lines.map((l) => {
      if (l.id !== itemId) return l;
      const updated = { ...l, lineMargin: margin };
      if (l.original && l.status !== 'added') {
        return setLineStatus(updated, 'changed');
      }
      return updated;
    }));
  }, []);

  // ─── Price update (per-line) ────────────────────────────────────────────────
  const handleUpdatePrice = useCallback((itemId, price) => {
    setCoLines((lines) => lines.map((l) => {
      if (l.id !== itemId) return l;
      const updated = { ...l, unitPrice: price };
      if (l.original && l.status !== 'added') {
        return setLineStatus(updated, 'changed');
      }
      return updated;
    }));
  }, []);

  // ─── Quantity update (per-line) ─────────────────────────────────────────────
  // IDI-457 follow-up (found by the live falsifier): this set ONLY
  // `quantity`, but everything that matters reads `sqft` — the card itself
  // (`item.sqft || item.quantity`), the pricing (materialCost / laborCost are
  // never recomputed), and the work-order materials projection. So an inline
  // quantity edit on a change order stored 1500 in `quantity` while `sqft`
  // and the line total stayed at 1000. That is IDI-310 exactly, which was
  // fixed for quotes (QuoteBuilderPage ~1422) and never for change orders.
  //
  // `rescaleLineQuantity` keeps quantity and sqft in step and scales the
  // line's own costs by new/old, deliberately WITHOUT re-running catalog
  // pricing (a migrated line must not be silently re-priced at today's
  // catalog; a deliberate re-price is what the Edit wizard is for).
  const handleUpdateQuantity = useCallback((itemId, quantity) => {
    setCoLines((lines) => lines.map((l) => {
      if (l.id !== itemId) return l;
      const updated = rescaleLineQuantity(l, quantity);
      if (l.original && l.status !== 'added') {
        return setLineStatus(updated, 'changed');
      }
      return updated;
    }));
  }, []);

  // ─── Save draft ─────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // PT-53A W1.4d: DEF-086 + DEF-096 fix. Route both handleSave and
      // handleFinalize through assembleChangeOrderPayload (single extracted
      // payload builder). Previously each callsite emitted an identical
      // inline `{ jobId, lines: coLines, ... }` object with coLines as raw
      // camelCase state; line-level money fields silently dropped on
      // round-trip (DEF-086), and fixing only one callsite left the other
      // corrupt (DEF-096). Extraction resolves both by construction.
      // IDI-567 / IDI-595: Save Draft never changes a CO's status. It sends
      // the stored status ('draft' if none), the same rule as the autosave
      // payload. The state machine has no edge back to draft (withdrawn,
      // active, 'none' → draft all 409'd); an unchanged status is allowed.
      const saveStatus = coMeta?.status || 'draft';
      const payload = assembleChangeOrderPayload({
        coId,
        jobId,
        coLines,
        co,
        terms,
        attachments,
        status: saveStatus,
        internalNotes,
      });
      await api.updateChangeOrder(coId, payload);
      toast.success('Change order saved');
    } catch (err) {
      toast.error('Failed to save change order');
    } finally {
      setSaving(false);
    }
  }, [coId, jobId, coLines, co, terms, attachments, internalNotes, toast, coMeta?.status]);

  // IDI-639: Reopen and Edit. The server sets "draft" and kills the link;
  // locally only the status flips. Autosave was off while read-only, so its
  // baseline starts from the reopened state: nothing is written until an edit.
  const handleReopen = useCallback(async () => {
    setReopening(true);
    try {
      await api.reopenChangeOrder(coId);
      setCoMeta((m) => ({ ...m, status: 'draft' }));
      setReopenOpen(false);
      toast.success(REOPEN_SUCCESS);
    } catch (err) {
      toast.error(REOPEN_FAILURE);
    } finally {
      setReopening(false);
    }
  }, [coId, toast]);

  // ─── Finalize → navigate to review ─────────────────────────────────────────
  const handleFinalize = useCallback(async () => {
    // DEF-160: flush any pending autosave before navigating to review.
    // onLeave() triggers an immediate save if dirty, then shows the toast.
    if (isDirty) {
      await onLeave();
    }
    navigate(`/change-orders/${coId}/review`);
  }, [coId, isDirty, onLeave, navigate]);

  // ─── Per-item CO status className (PT-45 45.3 tone) ───────────────────────
  const getItemClassName = useCallback((item) => {
    if (item.locked) return 'opacity-60 pointer-events-none';
    if (item.status === 'cancelled') return 'bg-gray-100 rounded-md opacity-70';
    if (item.status === 'added') return 'bg-green-50 rounded-md';
    if (item.status === 'changed') return 'bg-amber-50 rounded-md';
    return '';
  }, []);

  // ─── Per-item CO status overlay (badge + undo) ─────────────────────────────
  const renderItemOverlay = useCallback((item) => {
    if (item.locked) {
      return (
        <div className="flex items-center gap-2 px-2 py-1 text-xs text-gray-400">
          <span className="font-medium">Locked</span>
          {item.lockedByWo && <span className="text-gray-300">(completed — {item.lockedByWo})</span>}
        </div>
      );
    }
    if (item.status === 'cancelled') {
      return (
        <div className="flex items-center gap-2 px-2 py-1">
          <span className="text-xs font-medium text-gray-400">Cancelled</span>
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-gray-700 underline"
            onClick={() => handleUndoChange(item.id)}
          >
            Undo
          </button>
        </div>
      );
    }
    if (item.status === 'added') {
      return (
        <div className="px-2 py-1">
          <span className="text-xs font-medium text-green-700">New</span>
        </div>
      );
    }
    if (item.status === 'changed') {
      return (
        <div className="flex items-center gap-2 px-2 py-1">
          <span className="text-xs font-medium text-amber-700">Changed</span>
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-gray-700 underline"
            onClick={() => handleUndoChange(item.id)}
          >
            Undo
          </button>
        </div>
      );
    }
    return null;
  }, [handleUndoChange]);

  // ─── CO-03: per-row kebab menu items (Change Item / Cancel Item) ───
  // IDI-191: previously "Change Item" was a no-op status flip (setLineStatus
  // to 'changed' with no way to actually change anything), and was hidden
  // for any line that had already been changed/cancelled/added -- so for
  // most rows the menu showed only a redundant "Edit" that did the exact
  // same status transition as a side effect. "Change Item" now opens the
  // Add-Item wizard scoped to swap this line's product
  // (handleChangeItem/handleAddItem replace branch above), so it is a
  // genuinely different action from "Edit" (which only adjusts
  // sqft/margin/qty on the SAME product) and is offered whenever the line
  // is actually swappable.
  const getMenuItems = useCallback((item) => {
    if (item.locked || isReadOnly) return [{ label: 'Locked', hidden: true }];
    const items = [
      { label: 'Edit', onClick: () => setEditingItem(item) },
    ];
    // IDI-459 (E-27): "Change Item" was removed by the 2026-09-10 ruling.
    // Edit (IDI-191) stays and covers adjusting the line; swapping a product
    // on a change order means cancelling the line and adding the replacement,
    // which is what the diff should show anyway.
    // "Cancel Item" — mark as cancelled (original lines) or delete (added lines)
    if (item.status !== 'cancelled') {
      const isAdded = item.status === 'added' || !item.original;
      items.push({
        label: isAdded ? 'Delete' : 'Cancel Item',
        variant: 'danger',
        onClick: () => handleRemoveItem(item.id),
      });
    }
    // "Undo" — restore to original state
    if (item.status === 'changed' || item.status === 'cancelled') {
      items.push({ label: 'Undo', onClick: () => handleUndoChange(item.id) });
    }
    return items;
  }, [isReadOnly, handleRemoveItem, handleUndoChange]);

  // ─── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-sm text-gray-500">Loading change order…</div>
      </div>
    );
  }

  // ─── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="px-6 pt-6">
        <Alert variant="error">
          <strong>Error loading change order:</strong> {error}
        </Alert>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(`/jobs/${jobId}`)}>
          Back to Job
        </Button>
      </div>
    );
  }

  const coStatus = coMeta?.status || 'draft';
  // isReadOnly already defined above (DEF-160 autosave guard)

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pb-12">
      {/* ─── Header ──────────────────────────────────────────────────────────── */}
      <div className="px-6 pt-6">
        <PageHeader
          title={coTitle({ ...coMeta, id: coId })}
          /* IDI-823 (D1): name, else "Job " + its number; never the id. */
          subtitle={job?.name || job?.title || (getJobNumber(job) ? `Job ${getJobNumber(job)}` : undefined)}
          badge={<StatusPill status={coStatus} label={coStatusLabel(coStatus)} tone={coStatusTone(coStatus)} />}
          actions={
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={handleSave} loading={saving} disabled={isReadOnly}>
                Save Draft
              </Button>
              {/* IDI-459: the quote's More menu, minus reports. E-51 is
                  explicit that a change order has no reports, so "Quote
                  Analysis" is absent — not hidden, not stubbed. Material List
                  is genuinely useful here: it's the materials for the CHANGE,
                  which is what a crew needs to pull. */}
              <DropdownMenu
                ariaLabel="More actions"
                trigger={
                  <Button variant="secondary">
                    More <ChevronDownIcon className="h-4 w-4 ml-1 inline" aria-hidden="true" />
                  </Button>
                }
                items={[
                  {
                    label: 'Material List',
                    icon: <ClipboardDocumentListIcon className="h-4 w-4 flex-shrink-0" />,
                    onClick: () => setMaterialListOpen(true),
                  },
                ]}
              />
            </div>
          }
          backTo={`/jobs/${jobId}?tab=change-orders`}
        />
      </div>

      {/* IDI-309 (REQ-006): autosave failure is visible, not silent. */}
      {autoSaveError && (
        <div className="px-6 mt-4">
          <Alert variant="error">{String(autoSaveError)}</Alert>
        </div>
      )}

      {/* ─── Read-only banner ────────────────────────────────────────────────── */}
      {isReadOnly && (
        <div className="px-6 mt-4">
          <Alert variant="info">
            <div className="flex items-center justify-between gap-3">
              <span>This change order has been {coStatusLabel(coStatus)} and cannot be edited.</span>
              {canReopenCo(auth?.user, coMeta?.status) && (
                <Button size="sm" variant="secondary" onClick={() => setReopenOpen(true)}>Reopen and Edit</Button>
              )}
            </div>
          </Alert>
        </div>
      )}
      <ReopenConfirmDialog
        open={reopenOpen}
        status={coMeta?.status}
        loading={reopening}
        onClose={() => setReopenOpen(false)}
        onConfirm={handleReopen}
      />

      {/* ─── One grid, as on the quote builder (IDI-459 S1/D1) ──────────────
          QuoteBuilderPage keeps a single two-column grid: the tab strip and
          every tab body sit in the LEFT column, and the estimate rail (with
          Review & Send and Cancel) is always rendered on the RIGHT. The CO
          used to render the rail only on Builder, so it vanished on Notes,
          Files and Acknowledgments (co-UAT F-44). Tab bodies are still
          conditionally RENDERED, never CSS-hidden; every piece of state the
          builder owns lives on this page, so switching tabs loses nothing. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 px-6 mt-4">
        <div className="space-y-6">
          <Tabs tabs={CO_TABS} value={activeTab} onChange={handleTabChange} />

          {/* ─── Notes tab ──────────────────────────────────────────────────── */}
          {activeTab === 'notes' && (
            <Card className="p-4">
              <div className="mb-2">
                <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  Change order notes
                </span>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                  Internal — not shown to the customer
                </p>
              </div>
              <textarea
                className="w-full rounded-md border p-2 text-sm"
                style={{
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-surface-default)',
                  color: 'var(--color-text-primary)',
                }}
                rows={6}
                placeholder="Why this change is needed, who approved it, scheduling impact…"
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                disabled={isReadOnly}
              />
            </Card>
          )}

          {/* ─── Acknowledgments tab (IDI-542) ───────────────────────────────── */}
          {activeTab === 'acknowledgements' && (
            <Card className="p-4">
              {coId ? (
                <AcknowledgmentsPanel documentType="change_order" documentId={coId} />
              ) : (
                <EmptyState
                  title="No acknowledgment events yet"
                  description="Save and send this change order to start its acknowledgment history."
                />
              )}
            </Card>
          )}

          {/* ─── Files tab ──────────────────────────────────────────────────── */}
          {activeTab === 'files' && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  Files
                </span>
                <Button variant="secondary" size="sm" onClick={() => setAttachOpen(true)} disabled={isReadOnly}>
                  Attach
                </Button>
              </div>
              {coDocuments.length === 0 ? (
                <EmptyState
                  title="No files attached"
                  description="Attach files to this change order using the button above. Attached files will be available on Review & Send."
                />
              ) : (
                <ul className="space-y-2">
                  {coDocuments.map((doc, idx) => {
                    const d = entryDisplay(doc);
                    return (
                      <li
                        key={d.id || d.name || idx}
                        className="text-sm flex items-center justify-between"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        <span>{d.name}</span>
                        {d.meta && (
                          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                            {d.meta}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          )}

          {/* ─── Builder tab: area cards ────────────────────────────────────── */}
          {activeTab === 'builder' && (
            <div {...lockedLinesProps(isReadOnly)}>
              {areaGroups.length > 0 ? (
                areaGroups.map((group) => (
                  <AreaCard
                    key={group.areaId}
                    areaId={group.areaId}
                    areaLabel={group.label}
                    items={group.items}
                    computedItems={group.computedItems}
                    overallMargin={overallMargin}
                    onEditItem={(item) => setEditingItem(item)}
                    onRemoveItem={(itemId) => handleRemoveItem(itemId)}
                    /* IDI-457 (was INFERRED in IDI-455, confirmed): AreaCard calls
                       these with the item's LOCAL INDEX within the area group
                       (AreaCard.js:224-225 `itemIndex`), not its id. The quote
                       maps it through `group.indices[localIdx]`
                       (QuoteBuilderPage ~1959). These handlers match on
                       `l.id === itemId`, so an index never matched and EVERY
                       inline margin / price / quantity edit on a change order was
                       silently dropped. Map the index to the line's real id. */
                    onUpdateMargin={(localIdx, margin) => {
                      const id = group.items[localIdx]?.id;
                      if (id != null) handleUpdateMargin(id, margin);
                    }}
                    onUpdatePrice={(localIdx, price) => {
                      const id = group.items[localIdx]?.id;
                      if (id != null) handleUpdatePrice(id, price);
                    }}
                    onUpdateQuantity={(localIdx, qty) => {
                      const id = group.items[localIdx]?.id;
                      if (id != null) handleUpdateQuantity(id, qty);
                    }}
                    /* IDI-459: a change order is diff-only (ruling 08-21), so
                       per-area add (E-26) and alternates (E-31) are suppressed by
                       PROP — never a CSS hide, never a fork (ruling 09-10). Lines
                       are added from the single Add Item action below. */
                    allowPerLineAdd={false}
                    allowAlternates={false}
                    getItemClassName={getItemClassName}
                    renderItemOverlay={renderItemOverlay}
                    getMenuItems={!isReadOnly ? getMenuItems : undefined}
                  />
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500">
                  No line items. Add items to this change order.
                </div>
              )}

              {/* Add Item button */}
              {!isReadOnly && (
                <Button
                  variant="secondary"
                  onClick={() => setCascadeOpen(true)}
                  className="w-full"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              )}

              {/* IDI-137 (S2/D4): no Terms & Conditions box here. Terms are
                  picked and saved on Review & Send; a builder save echoes the
                  loaded terms (state below), so it never changes them. */}

              {/* IDI-459 (S3/D5): the legacy "Attachments / Add Attachment"
                  block is gone. Files attach on the Files tab (IDI-460) and
                  stored ones are listed there; `attachments` still round-trips
                  through the save payload so nothing stored is dropped. */}
            </div>
          )}
        </div>

        {/* Right column: Estimate Panel, on every tab (sticky via its own className) */}
        <EstimatePanel
          summary={estimateSummary}
          onApplyTarget={isReadOnly ? undefined : handleApplyCoTarget}
          targetEditDisabledReason={coTargetEditability.editable ? null : coTargetEditability.message}
          targetCostBasis={estimateSummary?.costBreakdown?.totalCost ?? 0}
          // IDI-534: the net change can be a credit; the engine accepts a
          // reachable one and refuses an unreachable one with the range.
          allowNegativeSubtotal
          onCancel={() => navigate(`/jobs/${jobId}?tab=change-orders`)}
          onFinalize={handleFinalize}
          finalizeLabel="Review & Send"
          finalizeDisabled={isReadOnly || coLines.length === 0}
          finalizeHint={isReadOnly ? 'Change order is read-only' : 'Needs at least one line item'}
        />
      </div>

      {/* ─── CO Summary (original / revised / net) ───────────────────────────── */}
      {/* IDI-459 (S2/D2, P-13): Builder tab only, in its existing place. */}
      {activeTab === 'builder' && (
      <div className="px-6 mt-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4 flex items-center justify-between text-sm">
          <div className="flex gap-6">
            <div>
              <span className="text-gray-500">Original Total</span>
              <span className="ml-2 font-medium">${((co.originalTotal || 0) / 100).toFixed(2)}</span>
            </div>
            <div>
              <span className="text-gray-500">Revised Total</span>
              <span className="ml-2 font-medium">${((co.revisedTotal || 0) / 100).toFixed(2)}</span>
            </div>
            <div>
              <span className={`text-gray-500`}>Net Change</span>
              <span className={`ml-2 font-medium ${co.netTotal < 0 ? 'text-green-600' : co.netTotal > 0 ? 'text-amber-600' : ''}`}>
                {co.netTotal < 0 ? '−' : '+'}${(Math.abs(co.netTotal || 0) / 100).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ─── Modals ──────────────────────────────────────────────────────────── */}

      {/* Add Item Cascade */}
      <AddItemCascade
        open={cascadeOpen}
        onClose={() => setCascadeOpen(false)}
        onAddItem={handleAddItem}
        marginContext={marginContext}
        /* IDI-457: the same catalog + pricing props the quote builder passes
           (QuoteBuilderPage ~2208). Without these AddItemCascade falls back
           to the static sample products and prices with no tenant margins. */
        accessoryCatalog={accessoryCatalog}
        tenantCategoryMargins={categoryMargins}
        tenantLaborDefaults={tenantLaborDefaults}
        tenantQuoteItems={insulationCatalog}
        catalogLoading={!catalogLoaded}
        lineItems={coLines}
        allowOptions={false}
        /* IDI-823 (D2, D3): change-order wording; no alternates (E-31). */
        targetNoun="change order"
        allowAlternates={false}
      />

      {/* IDI-176: Edit Item now opens a SECOND AddItemCascade instance in
          mode='edit' with `initialItem` prefilled. See AddItemCascade prop
          docblock for the edit-vs-add contract. The old flat side-panel
          (EditItemModal) is deleted this PR. */}
      <AddItemCascade
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        mode="edit"
        initialItem={editingItem}
        onEditItem={(id, updates) => {
          handleEditItemSave(id, updates);
          setEditingItem(null);
        }}
        onAddItem={() => { /* unreachable in edit mode; kept satisfied */ }}
        marginContext={marginContext}
        /* IDI-457: the edit instance needs the same catalog, or re-opening a
           line to edit it re-prices against the sample products. */
        accessoryCatalog={accessoryCatalog}
        tenantCategoryMargins={categoryMargins}
        tenantLaborDefaults={tenantLaborDefaults}
        tenantQuoteItems={insulationCatalog}
        catalogLoading={!catalogLoaded}
        lineItems={coLines}
        /* IDI-459 (E-30): no Options on a change order. */
        allowOptions={false}
        /* IDI-823 (D2, D3): change-order wording; no alternates (E-31). */
        targetNoun="change order"
        allowAlternates={false}
      />

      {/* Custom Item Modal */}
      <CustomItemModal
        open={customModalOpen}
        onClose={() => setCustomModalOpen(false)}
        onAdd={(item) => {
          handleAddItem({ ...item, status: 'added' });
          setCustomModalOpen(false);
        }}
      />

      {/* IDI-460: real attachment upload, scoped to the CHANGE ORDER. */}
      <AttachModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        scope="changeOrder"
        ownerId={coId}
        ownerLabel={coTitle({ ...coMeta, id: coId })}
        companyId={coMeta?.companyId}
        onAttached={async () => {
          setAttachOpen(false);
          // The server appends the ref to the CO's documents[] atomically;
          // re-read so the Files tab shows it without a page reload.
          try {
            const refreshed = await api.getChangeOrder(coId);
            const data = refreshed?.data || refreshed;
            if (data) setCoDocuments(mergeDocumentEntries(data));
          } catch { /* the tab refreshes on the next full load */ }
        }}
      />

      {/* IDI-459: Material List for the CHANGE (the materials a crew pulls). */}
      <MaterialListDialog
        open={materialListOpen}
        onClose={() => setMaterialListOpen(false)}
        quoteId={null}
        lineItems={coLines}
        catalog={insulationCatalog}
        accessoryCatalog={accessoryCatalog}
        title={coMeta?.name || 'Change order'}
        canSend={false}
      />

      {/* IDI-459: the Add Alternate modal is DELETED, not hidden behind a
          flag. Alternates are a quote concept (E-31) and this mount was also
          miswired — it passed the PARENT line as the alternate, so it could
          never have produced a correct one. Dead code behind a feature flag
          is how a miswired path survives a review. */}

      {/* Note Modal */}
      <NoteModal
        open={!!noteModalItem}
        onClose={() => setNoteModalItem(null)}
        initialNote={noteModalItem?.notes}
        itemLabel={noteModalItem?.productLabel}
        onSave={handleNoteSave}
      />
    </div>
  );
}
